// src/pages/Login.tsx
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/i18n";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading } = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // ✅ Resolver next de forma segura (soporta URL-encoded)
  const safeNext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("next");
    if (!raw) return null;

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }

    // ✅ seguridad: solo rutas internas
    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;

    return decoded;
  }, [location.search]);

  // ✅ Mensaje si venimos desde /auth/callback (confirmación email / magic link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const from = params.get("from");

    // from=auth lo ponemos desde AuthCallback para que el usuario vea feedback
    if (from === "auth") {
      setError("");
      // Nota: si no tienes keys de i18n para esto, dejamos fallback seguro:
      setInfo(t("login.authCallbackSuccess") || "Email confirmado. Ya puedes iniciar sesión.");
    }
  }, [location.search, t]);

  // ✅ Si ya está logueado, no lo dejes en /login (pero respeta next)
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;

    navigate(safeNext || "/mode", { replace: true });
  }, [user, authLoading, navigate, safeNext]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    setError("");
    setInfo("");

    const result = await login(email.trim(), password);
    setSubmitting(false);

    if (!result.ok) {
      if (result.reason === "email_not_confirmed") {
        setError(t("login.errors.emailNotConfirmedTitle"));
        setInfo(t("login.errors.emailNotConfirmedHelp"));
        return;
      }

      if (result.reason === "invalid_credentials") {
        setError(t("login.errors.invalidCredentials"));
        return;
      }

      setError(result.message || t("login.errors.generic"));
      return;
    }

    // ✅ Login exitoso → ir a ?next=... (si existe) o /mode
    navigate(safeNext || "/mode", { replace: true });
  };

  return (
    <div className="relative min-h-screen bg-[#0b0b0f] text-white px-6 flex items-center justify-center overflow-hidden">
      {/* Glow background (coherente con Home) */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="w-full max-w-md">
        {/* Volver (link minimal, elegante) */}
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition"
          type="button"
        >
          <span aria-hidden>←</span> {t("login.backToHome")}
        </button>

        <div className="bg-[#111118]/80 border border-white/10 p-10 rounded-2xl shadow-2xl backdrop-blur-sm">
          {/* Header marca (consistente con Home) */}
          <div className="text-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              Ovylon
              <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
            </div>
            <p className="mt-2 text-sm text-gray-400">{t("login.subtitle")}</p>
          </div>

          {/* Formulario */}
          <form className="flex flex-col gap-5" onSubmit={handleLogin}>
            <div>
              <label className="block text-xs text-gray-400 mb-2">{t("login.emailLabel")}</label>
              <input
                type="email"
                placeholder={t("login.emailPlaceholder")}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">{t("login.passwordLabel")}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("login.passwordPlaceholder")}
                  className="w-full px-4 py-3 pr-14 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none appearance-none"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-lpignore="true"
                />

                {/* Ojito (único) */}
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  title={showPassword ? t("login.hide") : t("login.show")}
                >
                  {showPassword ? (
                    // Eye-off
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path
                        d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M9.88 5.09A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a17.3 17.3 0 0 1-3.17 4.12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M6.11 6.11C3.77 7.74 2 12 2 12s3 7 10 7a10.23 10.23 0 0 0 3.89-.78"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    // Eye
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            {info && <p className="text-emerald-300 text-xs text-center">{info}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t("login.loading") : t("login.submit")}
            </button>
          </form>

          {/* Links */}
          <div className="text-center mt-6 space-y-2">
            <Link to="/signup" className="text-indigo-300 hover:text-indigo-200 hover:underline text-sm">
              {t("login.createAccount")}
            </Link>

            <div>
              <Link to="/forgot-password" className="text-indigo-300 hover:text-indigo-200 hover:underline text-sm">
                {t("login.forgotPassword")}
              </Link>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">{t("login.footerNote")}</p>
      </div>
    </div>
  );
}





